import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createConsultationReview, fetchRatedConsultations } from './consultationsReviews.thunk';
import { ISpecialist } from '../specialists/types';
import { Session } from '@mindly/ui-components';

type Status = 'default' | 'good-review' | 'bad-review';

export type ReviewData = {
  id?: string;
  consultation_id: string;
  specialist_id: string;
  client_id: string;
  stars_count: number;
  reasons?: string[];
  text_of_review: string;
  client_avatar: string;
  created_at: number;
  client_name?: string;
  currentSpecialist?: ISpecialist | null;
};

type InitialState = {
  view: Status;
  isLoading: boolean;
  isError: boolean;
  isOpenReviewModal: boolean;
  reviewData: ReviewData | null;
  currentSpecialist: ISpecialist | null;
  ratedConsultations: Omit<ReviewData, 'currentSpecialist'>[];

  isTrialSessionReviewModalOpen: boolean;
  isSubscriptionSessionReviewModalOpen: boolean;
  sessionForReview: Session | null;
};

const initialState: InitialState = {
  view: 'default',
  isLoading: false,
  isError: false,
  isOpenReviewModal: false,
  reviewData: null,
  currentSpecialist: null,
  ratedConsultations: [],

  isTrialSessionReviewModalOpen: false,
  isSubscriptionSessionReviewModalOpen: false,
  sessionForReview: null,
};

const consultationsReviewsSlice = createSlice({
  name: 'consultationsReviews',
  initialState,
  reducers: {
    setConsultationViewData: (state, action: PayloadAction<Status>) => {
      state.view = action.payload;
    },
    setReviewModalIsOpen: (state, action) => {
      state.isOpenReviewModal = action.payload;
    },
    setConsultationReview: (state, action) => {
      state.reviewData = action.payload;
    },
    setTrialSessionReviewModalIsOpen: (state, action) => {
      state.isTrialSessionReviewModalOpen = action.payload;
    },
    setSubscriptionSessionReviewModalIsOpen: (state, action) => {
      state.isSubscriptionSessionReviewModalOpen = action.payload;
    },
    setSessionForReview: (state, action) => {
      const sessionForReview = action.payload as Session;

      if (sessionForReview && !sessionForReview.session_review) {
        state.sessionForReview = sessionForReview;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createConsultationReview.pending, (state, action) => {
      state.isError = false;
      state.isLoading = true;
    });
    builder.addCase(createConsultationReview.fulfilled, (state, action) => {
      state.isLoading = false;
      state.ratedConsultations = [...state.ratedConsultations, action.payload.review];
    });
    builder.addCase(createConsultationReview.rejected, (state, action) => {
      state.isError = true;
      state.isLoading = false;
    });
    builder.addCase(fetchRatedConsultations.fulfilled, (state, action) => {
      state.ratedConsultations = action.payload;
    });
  },
});

export const {
  setConsultationReview,
  setConsultationViewData,
  setTrialSessionReviewModalIsOpen,
  setSubscriptionSessionReviewModalIsOpen,
  setSessionForReview,
} = consultationsReviewsSlice.actions;

export default consultationsReviewsSlice.reducer;
